import { create } from 'zustand'
import type { ChatAttachment } from '../api/types'

export type OutboxStatus = 'pending' | 'failed'

export type OutboxMessage = {
  clientRef: string
  conversationId: number
  body: string
  attachmentIds: number[]
  attachments: ChatAttachment[]
  mentions: number[]
  replyToMessageId: number | null
  status: OutboxStatus
  error: string | null
  sentAt: string
}

export type StagedFile = {
  id: string
  file: File
  contentType: string
  attachmentId: number | null
  attachment: ChatAttachment | null
  error: string | null
  uploading: boolean
}

export type ComposerDraft = {
  body: string
  replyToMessageId: number | null
  staged: StagedFile[]
}

const EMPTY_DRAFT: ComposerDraft = { body: '', replyToMessageId: null, staged: [] }

type ChatOutboxState = {
  drafts: Record<number, ComposerDraft>
  outbox: Record<number, OutboxMessage[]>
  draftFor: (conversationId: number) => ComposerDraft
  setDraft: (conversationId: number, body: string) => void
  setReplyTo: (conversationId: number, messageId: number | null) => void
  stageFiles: (conversationId: number, files: StagedFile[]) => void
  patchStaged: (conversationId: number, id: string, patch: Partial<StagedFile>) => void
  unstageFile: (conversationId: number, id: string) => void
  clearComposer: (conversationId: number) => void
  addPending: (message: OutboxMessage) => void
  markSent: (conversationId: number, clientRef: string) => void
  markFailed: (conversationId: number, clientRef: string, error: string) => void
  markRetrying: (conversationId: number, clientRef: string) => void
  discard: (conversationId: number, clientRef: string) => void
  clearAll: () => void
}

export const useChatOutboxStore = create<ChatOutboxState>((set, get) => ({
  drafts: {},
  outbox: {},
  draftFor: (conversationId) => get().drafts[conversationId] ?? EMPTY_DRAFT,
  setDraft: (conversationId, body) =>
    set((state) => ({
      drafts: {
        ...state.drafts,
        [conversationId]: { ...(state.drafts[conversationId] ?? EMPTY_DRAFT), body },
      },
    })),
  setReplyTo: (conversationId, replyToMessageId) =>
    set((state) => ({
      drafts: {
        ...state.drafts,
        [conversationId]: { ...(state.drafts[conversationId] ?? EMPTY_DRAFT), replyToMessageId },
      },
    })),
  stageFiles: (conversationId, files) =>
    set((state) => {
      const draft = state.drafts[conversationId] ?? EMPTY_DRAFT
      return {
        drafts: {
          ...state.drafts,
          [conversationId]: { ...draft, staged: [...draft.staged, ...files].slice(0, 10) },
        },
      }
    }),
  patchStaged: (conversationId, id, patch) =>
    set((state) => {
      const draft = state.drafts[conversationId] ?? EMPTY_DRAFT
      return {
        drafts: {
          ...state.drafts,
          [conversationId]: {
            ...draft,
            staged: draft.staged.map((item) => (item.id === id ? { ...item, ...patch } : item)),
          },
        },
      }
    }),
  unstageFile: (conversationId, id) =>
    set((state) => {
      const draft = state.drafts[conversationId] ?? EMPTY_DRAFT
      return {
        drafts: {
          ...state.drafts,
          [conversationId]: { ...draft, staged: draft.staged.filter((item) => item.id !== id) },
        },
      }
    }),
  clearComposer: (conversationId) =>
    set((state) => ({ drafts: { ...state.drafts, [conversationId]: EMPTY_DRAFT } })),
  addPending: (message) =>
    set((state) => ({
      outbox: {
        ...state.outbox,
        [message.conversationId]: [...(state.outbox[message.conversationId] ?? []), message],
      },
    })),
  markSent: (conversationId, clientRef) =>
    set((state) => ({
      outbox: {
        ...state.outbox,
        [conversationId]: (state.outbox[conversationId] ?? []).filter((m) => m.clientRef !== clientRef),
      },
    })),
  markFailed: (conversationId, clientRef, error) =>
    set((state) => ({
      outbox: {
        ...state.outbox,
        [conversationId]: (state.outbox[conversationId] ?? []).map((m) =>
          m.clientRef === clientRef ? { ...m, status: 'failed', error } : m,
        ),
      },
    })),
  markRetrying: (conversationId, clientRef) =>
    set((state) => ({
      outbox: {
        ...state.outbox,
        [conversationId]: (state.outbox[conversationId] ?? []).map((m) =>
          m.clientRef === clientRef ? { ...m, status: 'pending', error: null } : m,
        ),
      },
    })),
  discard: (conversationId, clientRef) =>
    set((state) => ({
      outbox: {
        ...state.outbox,
        [conversationId]: (state.outbox[conversationId] ?? []).filter((m) => m.clientRef !== clientRef),
      },
    })),
  clearAll: () => set({ drafts: {}, outbox: {} }),
}))

export function hasFailedSend(outbox: Record<number, OutboxMessage[]>, conversationId: number): boolean {
  return (outbox[conversationId] ?? []).some((message) => message.status === 'failed')
}
